import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { getGroupName, LayerGroupProps } from "./layerModels";
import { setLayerVisibility } from "features/krpano/details-slice";
import Accordion from "shared/components/Accordion";
import DataItem from "shared/components/DataItem";
import Switch from "shared/components/Switch";
import Layer from "./Layer";
import React, { useCallback } from "react";

/**
 * Control the Layers
 * e.g. Field Boundary, Transport, Land, Sports, Power, etc.
 */
export const LayerGroup: React.FC<LayerGroupProps> = ({ type }) => {
  // NOTE: This type trickery is to allow the type of `groupLayers` to be
  // properly inferred to a union of `(FieldLayer | FeatureGroup)[]`
  const groupLayers: RootState["krpano"]["layers"][typeof type][number][] =
    useAppSelector((state) => state.krpano.layers[type]);
  const dispatch = useAppDispatch();
  const groupVisible = groupLayers.every((layer) => layer.visible);

  const handleGroupToggle = useCallback(
    (newValue: boolean) => {
      dispatch(
        setLayerVisibility([
          {
            type,
            visible: newValue,
          },
        ])
      );
    },
    [dispatch, type]
  );

  // If the group is empty, don't show at all
  if (groupLayers.length === 0) {
    return null;
  }

  return (
    <Accordion
      headerChildren={
        <DataItem
          left={<h4>{getGroupName(type)}</h4>}
          middle={
            <Switch
              name={`ShowHide${type}Toggle`}
              size="smallMedium"
              checked={groupVisible}
              onChange={handleGroupToggle}
              // If group is empty, disable toggle
              // A fallback in case the group is always shown in future
              disabled={groupLayers.length === 0}
            />
          }
          additionalClasses="LayerGroup"
        />
      }
      bodyChildren={
        <div className="DisplayFlex FlexColumn VSmallGap">
          {groupLayers.map((layer, layerIndex) => (
            <Layer
              key={`LAYER_${type}_${layerIndex}`}
              layerName={layer.name}
              type={type}
              targetIndex={layerIndex}
              visible={layer.visible}
            />
          ))}
        </div>
      }
      additionalBodyClasses="HasInset"
      withArrow
      subLevel={1}
      // initialOpen
      mountOnEnter
      // unmountOnExit
    />
  );
};
